import {
  GLOBAL_ASSISTANT_PAGE_CATALOG,
  type GlobalAssistantPageCatalogEntry,
} from './pages/catalog';

type CatalogOptionId =
  (typeof GLOBAL_ASSISTANT_PAGE_CATALOG)[number]['optionId'];

// These are execution identities, not routing policies. Names, paths and stored
// page IDs come from the page catalog; the model still chooses the destination.
const definitions = [
  {
    target: 'talent_discovery',
    discoveryKind: 'talent',
    optionId: '__std__:app:talent-discovery',
    filterKind: 'tdV2',
    entityType: 'talentDiscovery',
  },
  {
    target: 'company_discovery',
    discoveryKind: 'company',
    optionId: '__std__:company:screener',
    filterKind: 'screener',
    entityType: 'company',
  },
  {
    target: 'role_discovery',
    discoveryKind: 'role',
    optionId: '__std__:role:screener',
    filterKind: 'screener',
    entityType: 'role',
  },
  {
    target: 'geography_discovery',
    discoveryKind: 'geography',
    optionId: '__std__:geography:screener',
    filterKind: 'screener',
    entityType: 'geography',
  },
  {
    target: 'posting_discovery',
    discoveryKind: 'posting',
    optionId: '__std__:app:posting-discovery',
    filterKind: 'tdV2',
    entityType: 'postingDiscovery',
  },
] as const satisfies readonly {
  target: string;
  discoveryKind: string;
  optionId: CatalogOptionId;
  filterKind: 'tdV2' | 'screener';
  entityType: string;
}[];

export const ASSISTANT_DISCOVERY_TARGETS = definitions.map((definition) => {
  // Availability hides offers, not the identity needed to recognize a route
  // and retain its parser, access check and command destination.
  const page: GlobalAssistantPageCatalogEntry | undefined =
    GLOBAL_ASSISTANT_PAGE_CATALOG.find(
      (entry) => entry.optionId === definition.optionId
    );
  if (!page) {
    throw new Error(
      `Discovery target requires a catalog page: ${definition.optionId}`
    );
  }
  const [, entity, segment] = page.optionId.split(':');
  return {
    ...definition,
    enabled: page.enabled,
    name: page.pageName,
    path:
      page.navigatePathOverride ??
      (entity === 'app' ? `/${segment}` : `/${entity}/${segment}`),
    revelioPageIds: page.revelioPageIds,
  };
});

export type AssistantDiscoveryTarget =
  (typeof ASSISTANT_DISCOVERY_TARGETS)[number];
export type AssistantDiscoveryKind = AssistantDiscoveryTarget['discoveryKind'];
// Schemas offer only enabled destinations; this may be empty. Relative and
// chart targets are added by their callers and keep those schemas nonempty.
export const DISCOVERY_FILTER_TARGETS = ASSISTANT_DISCOVERY_TARGETS.filter(
  ({ enabled }) => enabled
).map(({ target }) => target);

export const getDiscoveryTarget = (
  target: unknown
): AssistantDiscoveryTarget | undefined =>
  ASSISTANT_DISCOVERY_TARGETS.find((entry) => entry.target === target);

export const getDiscoveryTargetForKind = (
  kind: unknown
): AssistantDiscoveryTarget | undefined =>
  ASSISTANT_DISCOVERY_TARGETS.find((entry) => entry.discoveryKind === kind);

export const getDiscoveryTargetForOptionId = (
  optionId: unknown
): AssistantDiscoveryTarget | undefined =>
  ASSISTANT_DISCOVERY_TARGETS.find((entry) => entry.optionId === optionId);

export const getDiscoveryTargetForPath = (
  pathname: string
): AssistantDiscoveryTarget | undefined => {
  const path = `/${pathname.split('/').filter(Boolean).join('/')}`;
  return ASSISTANT_DISCOVERY_TARGETS.find((entry) => entry.path === path);
};

export const getDiscoveryTargetForPageId = (
  pageId: string | undefined
): AssistantDiscoveryTarget | undefined =>
  ASSISTANT_DISCOVERY_TARGETS.find(
    (entry) => pageId !== undefined && entry.revelioPageIds.includes(pageId)
  );

export const resolveDiscoveryFilterTarget = (
  target: unknown,
  currentDiscoveryKind?: AssistantDiscoveryKind
): AssistantDiscoveryTarget | undefined =>
  target === 'current_page'
    ? getDiscoveryTargetForKind(currentDiscoveryKind)
    : getDiscoveryTarget(target);
